import React, { useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Document } from '../../../gql/graphql';
import { Modal } from '../../common/Modal';
import { MarkdownRenderer } from '../../common/MarkdownRenderer';
import { useAdminTranslation } from '../../../modules/i18n';
import { GET_DOCUMENT_CONTENT, GET_DOCUMENT_VERSIONS } from '../../../graphql/queries';

type TabId = 'info' | 'content' | 'history';

interface DocumentDetailModalProps {
  document: Document | null;
  isOpen: boolean;
  onClose: () => void;
}

const DocumentDetailModal: React.FC<DocumentDetailModalProps> = ({ document, isOpen, onClose }) => {
  const { t } = useAdminTranslation();
  const [activeTab, setActiveTab] = useState<TabId>('info');

  const [fetchContent, { data: contentData, loading: contentLoading }] = useLazyQuery(GET_DOCUMENT_CONTENT);
  const [fetchVersions, { data: versionsData, loading: versionsLoading }] = useLazyQuery(GET_DOCUMENT_VERSIONS);

  useEffect(() => {
    if (isOpen && document?.id) {
      setActiveTab('info');
      fetchContent({ variables: { id: document.id } });
    }
  }, [isOpen, document?.id, fetchContent]);

  useEffect(() => {
    if (activeTab === 'history' && document?.id) {
      fetchVersions({ variables: { documentId: document.id, take: 20 } });
    }
  }, [activeTab, document?.id, fetchVersions]);

  if (!isOpen || !document) return null;

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'N/A';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round((bytes / Math.pow(1024, i)) * 100) / 100} ${sizes[i]}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('vi-VN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(date);
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return t('content.documents.status.completed');
      case 'PROCESSING':
        return t('content.documents.status.processing');
      case 'ERROR':
        return t('content.documents.status.error');
      case 'PENDING':
        return t('content.documents.status.pending');
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'PROCESSING':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'ERROR':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'PENDING':
        return 'bg-gray-100 text-gray-800 dark:bg-dark-background-tertiary dark:text-dark-text';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-dark-background-tertiary dark:text-dark-text';
    }
  };

  const fetchedText = contentData?.findManyDocuments?.[0]?.text;
  const versions = versionsData?.getDocumentVersions ?? [];

  const tabs: { id: TabId; label: string }[] = [
    { id: 'info', label: t('content.documents.detail.tabs.info') },
    { id: 'content', label: t('content.documents.detail.tabs.content') },
    { id: 'history', label: t('content.documents.detail.tabs.history') }
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('content.documents.detail.title')}
      headerContent={<div className='text-2xl'>📄</div>}
      size='lg'
      footer={
        <button
          onClick={onClose}
          className='rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:bg-dark-background-tertiary dark:text-dark-text-secondary dark:hover:bg-dark-background-hover'
        >
          {t('common.close')}
        </button>
      }
    >
      {/* Tabs */}
      <div className='mb-4 flex border-b border-gray-200 dark:border-border-dark'>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'border-primary text-primary dark:text-primary-light'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'info' && (
        <div className='space-y-4'>
          {/* Title */}
          <div>
            <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>
              {t('content.documents.detail.fields.documentName')}
            </label>
            <p className='rounded-lg bg-gray-50 p-3 text-sm text-gray-900 dark:bg-dark-background-tertiary dark:text-dark-text'>
              {document.title}
            </p>
          </div>

          {/* Extension */}
          <div>
            <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>
              {t('content.documents.detail.fields.extension')}
            </label>
            <span className='inline-flex rounded-full bg-gray-100 px-3 py-1 text-sm font-semibold text-gray-800 dark:bg-dark-background-tertiary dark:text-dark-text'>
              {document.extension}
            </span>
          </div>

          {/* Status */}
          <div>
            <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>
              {t('content.documents.detail.fields.status')}
            </label>
            <span className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold ${getStatusColor(document.status)}`}>
              {getStatusText(document.status)}
            </span>
          </div>

          {/* File Size */}
          <div>
            <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>
              {t('content.documents.detail.fields.fileSize')}
            </label>
            <p className='rounded-lg bg-gray-50 p-3 text-sm text-gray-900 dark:bg-dark-background-tertiary dark:text-dark-text'>
              {formatFileSize(document.fileSize)}
            </p>
          </div>

          {/* Created Date */}
          <div>
            <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>
              {t('content.documents.detail.fields.createdDate')}
            </label>
            <p className='rounded-lg bg-gray-50 p-3 text-sm text-gray-900 dark:bg-dark-background-tertiary dark:text-dark-text'>
              {formatDate(document.createdAt)}
            </p>
          </div>

          {/* Updated Date */}
          <div>
            <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>
              {t('content.documents.detail.fields.updatedDate')}
            </label>
            <p className='rounded-lg bg-gray-50 p-3 text-sm text-gray-900 dark:bg-dark-background-tertiary dark:text-dark-text'>
              {formatDate(document.updatedAt)}
            </p>
          </div>

          {/* Source */}
          {document.source && (
            <div>
              <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>
                {t('content.documents.detail.fields.source')}
              </label>
              <p className='rounded-lg bg-gray-50 p-3 text-sm text-gray-900 dark:bg-dark-background-tertiary dark:text-dark-text'>
                {document.source}
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'content' && (
        <div className='max-h-[60vh] overflow-y-auto'>
          {contentLoading ? (
            <div className='flex items-center justify-center py-12'>
              <svg className='h-8 w-8 animate-spin text-primary' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
                <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                <path
                  className='opacity-75'
                  fill='currentColor'
                  d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                ></path>
              </svg>
            </div>
          ) : fetchedText ? (
            <MarkdownRenderer content={fetchedText} className='prose prose-sm max-w-none dark:prose-invert' />
          ) : (
            <p className='py-12 text-center text-sm text-gray-500 dark:text-gray-400'>
              {t('content.documents.detail.noContent')}
            </p>
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <div className='max-h-[60vh] overflow-y-auto'>
          {versionsLoading ? (
            <div className='flex items-center justify-center py-12'>
              <svg className='h-8 w-8 animate-spin text-primary' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
                <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                <path
                  className='opacity-75'
                  fill='currentColor'
                  d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                ></path>
              </svg>
            </div>
          ) : versions.length === 0 ? (
            <p className='py-12 text-center text-sm text-gray-500 dark:text-gray-400'>
              {t('content.documents.detail.versionHistory.noHistory')}
            </p>
          ) : (
            <div className='space-y-3'>
              {versions.map((v: { id: string; createdAt: string; changeType: string; changedBy: string }) => (
                <div key={v.id} className='flex items-center gap-3 rounded-lg bg-gray-50 p-3 dark:bg-dark-background-tertiary'>
                  <span className='text-sm text-gray-600 dark:text-gray-300'>{formatDate(v.createdAt)}</span>
                  <span
                    className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                      v.changeType === 'deleted'
                        ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                    }`}
                  >
                    {v.changeType === 'deleted'
                      ? t('content.documents.detail.versionHistory.deleted')
                      : t('content.documents.detail.versionHistory.updated')}
                  </span>
                  <span className='text-xs text-gray-400 dark:text-gray-500' title={v.changedBy}>
                    {v.changedBy.slice(0, 8)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default DocumentDetailModal;
